import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import type { VariablesOf } from '@graphql-typed-document-node/core';

import { getDocumentName } from '@e-socna/data-layer';

import { AuthenticationContextProvider_AuthenticatedUser_QueryDocument } from '../../../../../contexts/AuthenticationContext';
import { useAuthenticationContext } from '../../../../../contexts/AuthenticationContext/hooks/useAuthenticationContext.hook';

import { MyAccountPage_OneUser_QueryDocument } from '../../../../MyAccount/queries/one-user.query';

import { UserPage_OneUser_QueryDocument } from '../../..';

import { useSaveAuthenticatedUserUiSettingsMutation } from './useSaveAuthenticatedUserUiSettingsMutation.hook';
import type { UserCmp_Settings_AuthenticatedUser_UiSettings_Save_Mutation } from './useSaveAuthenticatedUserUiSettingsMutation.hook';

export type UiSettingsValues = VariablesOf<
  typeof UserCmp_Settings_AuthenticatedUser_UiSettings_Save_Mutation
>;

export const useSaveUiSettings = ({
  isContextualColorEnabled,
  paletteModeId,
  themeMainColor,
}: Readonly<UiSettingsValues>) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const authenticatedUserId =
    useAuthenticationContext().authenticationData?._id;

  const { mutate: saveAuthenticatedUserUiSettingsMutation } =
    useSaveAuthenticatedUserUiSettingsMutation();

  const saveUiSettings = useCallback(
    (changes: Readonly<Partial<UiSettingsValues>>) => {
      saveAuthenticatedUserUiSettingsMutation(
        { isContextualColorEnabled, paletteModeId, themeMainColor, ...changes },
        {
          onError: () => {
            enqueueSnackbar("La mise à jour de l'affichage a échoué.", {
              variant: 'error',
            });
          },
          onSuccess: () => {
            void queryClient.invalidateQueries({
              queryKey: [
                getDocumentName(UserPage_OneUser_QueryDocument),
                { id: authenticatedUserId },
              ],
            });
            void queryClient.invalidateQueries({
              queryKey: [getDocumentName(MyAccountPage_OneUser_QueryDocument)],
            });
            void queryClient.invalidateQueries({
              queryKey: [
                getDocumentName(
                  AuthenticationContextProvider_AuthenticatedUser_QueryDocument,
                ),
              ],
            });
            enqueueSnackbar("L'affichage a bien été mis à jour.");
          },
        },
      );
    },
    [
      authenticatedUserId,
      enqueueSnackbar,
      isContextualColorEnabled,
      paletteModeId,
      queryClient,
      saveAuthenticatedUserUiSettingsMutation,
      themeMainColor,
    ],
  );

  return { saveUiSettings };
};
